// Components
import { Tab, TabPanel, TabPanels, Tabs, TabsList } from '@octano/global-ui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { Card } from 'reactstrap';
import useQuery from '../../hooks/useQuery';
import useUserState from '../../hooks/useUserState';
import { TranslationsKeys } from '../../locales/translations';
import { PermissionName } from '../../types/Auth';
import StudentWarningsConfig from './StudentWarningsConfig';

// Types
import StudentWarningsSummary from './StudentWarningsSummary';
import { StudentWarningsSummaryFormLoader } from './loaders/StudentWarningsSummaryFormLoader';
import StudentRiskEvaluationSessions from './StudentRiskEvaluationSessions';
import { StudentRiskHistory } from './StudentRiskHistory';
import { StudentRiskObservations } from './parts/StudentRiskObservations';

enum TabName {
  Config = 'config',
  Summary = 'summary',
  RiskEvaluation = 'risk-evaluation',
  RiskHistory = 'risk-history',
  RiskObservations = 'risk-observations',
}

type TabRoute = {
  Component: () => JSX.Element;
  name: TabName;
  requiredPermissions: PermissionName[];
  allPermisionsRequired?: boolean;
  configs?: Record<string, any>;
};

// Constants
const TAB_ROUTES: TabRoute[] = [
  {
    Component: () => <StudentWarningsConfig />,
    name: TabName.Config,
    requiredPermissions: [PermissionName.STUDENT_WARNINGS_CONFIG_ALL],
  },
  {
    Component: () => <StudentWarningsSummary />,
    name: TabName.Summary,
    requiredPermissions: [PermissionName.STUDENT_WARNINGS_ALERTS_ALL],
  },
  {
    Component: () => <StudentRiskEvaluationSessions />,
    name: TabName.RiskEvaluation,
    requiredPermissions: [PermissionName.STUDENT_RISK_EVALUATION_SESSION_ALL],
  },
  {
    Component: () => <StudentRiskHistory />,
    name: TabName.RiskHistory,
    requiredPermissions: [PermissionName.STUDENT_RISK_HISTORY_ALL],
  },
  {
    Component: () => <StudentRiskObservations />,
    name: TabName.RiskObservations,
    requiredPermissions: [PermissionName.STUDENT_RISK_OBSERVATIONS_ALL],
  },
];

const StudentWarnings = () => {
  const history = useHistory();
  const { path } = useRouteMatch();
  const params = useQuery();
  const { isAuthorizedTo } = useUserState();

  const { t } = useTranslation(TranslationsKeys.STUDENT_ALERTS);

  const authorizedRoute = useMemo(() => {
    const permissionFiltered = TAB_ROUTES.filter(
      (route) =>
        !route.requiredPermissions?.length ||
        isAuthorizedTo(route.requiredPermissions, route.allPermisionsRequired),
    );

    return permissionFiltered;
  }, [isAuthorizedTo]);

  const defaultTab = useMemo(() => {
    if (!authorizedRoute?.length) {
      return TabName.Summary;
    }
    if (authorizedRoute?.some((e) => e.name === TabName.Summary)) {
      return TabName.Summary;
    }
    return authorizedRoute[0].name;
  }, [authorizedRoute]);

  const handleTabSelection = (selectedTab: number | string) => {
    history.push({ pathname: path, search: `?tab=${selectedTab}` });
    return null;
  };

  const initialTab = useMemo(() => {
    const queryTab = params.get('tab');
    if (!queryTab) return defaultTab;

    const isValidTab = authorizedRoute.some((ar) => ar.name === queryTab);
    return isValidTab ? queryTab : defaultTab;
  }, [params, defaultTab, authorizedRoute]);

  return (
    <div className="mx-3">
      <Tabs defaultTab={initialTab} onSelection={handleTabSelection}>
        <TabsList>
          {authorizedRoute.map((route) => (
            <Tab id={route.name} key={route.name}>
              {t(`tabs.${route.name}`)}
            </Tab>
          ))}
        </TabsList>
        <TabPanels>
          <StudentWarningsSummaryFormLoader fetchImmediately>
            <Card className="p-4">
              {authorizedRoute.map(({ Component, ...route }) => (
                <TabPanel
                  id={route.name}
                  key={route.name}
                  children={<Component />}
                />
              ))}
            </Card>
          </StudentWarningsSummaryFormLoader>
        </TabPanels>
      </Tabs>
    </div>
  );
};

export default StudentWarnings;
